import { z } from 'zod';
import { statusIdSchema, uuidSchema, dateSchema, optionalDateSchema, MAX_NOTES_LENGTH, MAX_INVOICE_NUMBER_LENGTH, MAX_CURRENCY_LENGTH, MAX_PAYMENT_TERMS_LENGTH, MAX_PO_NUMBER_LENGTH, } from './common.js';
/**
 * Schema for creating a new invoice.
 *
 * Mirrors `public.invoices` user-editable columns. The original schema
 * was missing 13 real DB columns (`discount`, `discount_expiration`,
 * `discount_amt`, `price_book_id`, `external_system`,
 * `external_system_record_id`, `quote_version_id`, `quote_id`,
 * `extraction_method`, `extraction_confidence`, `ai_provider`,
 * `source_filename`, `source_file_path`); without them and without
 * `.passthrough()`, payloads carrying those fields silently dropped
 * them before reaching the DB (same Phase 1.5 silent-strip class as
 * vendorSchema and priceBookSchema).
 *
 * Note: the AI/extraction fields (`extraction_method`,
 * `extraction_confidence`, `ai_provider`, `source_filename`,
 * `source_file_path`) are modelled here so JWT-side AI extraction
 * routes can write them. The external API surface
 * (`apps/server/src/routes/external/v1/invoices.ts`) blocks them
 * separately via a FORBIDDEN_FIELDS guard — third-party clients
 * shouldn't be writing extraction-system attribution.
 */
export const invoiceCreateSchema = z.object({
    vendor_id: uuidSchema.optional().nullable(),
    invoice_number: z
        .string()
        .max(MAX_INVOICE_NUMBER_LENGTH, `Invoice number must be ${MAX_INVOICE_NUMBER_LENGTH} characters or less`)
        .optional()
        .nullable(),
    invoice_date: optionalDateSchema,
    service_date: optionalDateSchema,
    due_date: optionalDateSchema,
    total_amount: z
        .number()
        .min(0, 'Total amount must be non-negative')
        .optional()
        .nullable(),
    subtotal: z
        .number()
        .min(0, 'Subtotal must be non-negative')
        .optional()
        .nullable(),
    tax_amount: z
        .number()
        .min(0, 'Tax amount must be non-negative')
        .optional()
        .nullable(),
    // Status is now managed via status_id foreign key
    status_id: statusIdSchema,
    notes: z
        .string()
        .max(MAX_NOTES_LENGTH, `Notes must be ${MAX_NOTES_LENGTH} characters or less`)
        .optional()
        .nullable(),
    currency: z
        .string()
        .max(MAX_CURRENCY_LENGTH, `Currency must be ${MAX_CURRENCY_LENGTH} characters or less`)
        .optional()
        .nullable(),
    payment_terms: z
        .string()
        .max(MAX_PAYMENT_TERMS_LENGTH, `Payment terms must be ${MAX_PAYMENT_TERMS_LENGTH} characters or less`)
        .optional()
        .nullable(),
    po_number: z
        .string()
        .max(MAX_PO_NUMBER_LENGTH, `PO number must be ${MAX_PO_NUMBER_LENGTH} characters or less`)
        .optional()
        .nullable(),
    // Discount fields — invoice-level percentage and resulting amount.
    discount: z.number().min(0).max(100).optional().nullable(),
    discount_expiration: optionalDateSchema,
    discount_amt: z.number().min(0).optional().nullable(),
    // FK relationships beyond vendor_id.
    price_book_id: uuidSchema.optional().nullable(),
    quote_version_id: uuidSchema.optional().nullable(),
    quote_id: uuidSchema.optional().nullable(),
    // External-system bookkeeping (NetSuite, etc.).
    external_system: z.string().nullable().optional(),
    external_system_record_id: z.string().nullable().optional(),
    // AI extraction attribution. Modelled here so JWT-side extraction
    // routes can write them; rejected at the external API layer.
    extraction_method: z.string().nullable().optional(),
    extraction_confidence: z.number().nullable().optional(),
    ai_provider: z.string().nullable().optional(),
    source_filename: z.string().nullable().optional(),
    source_file_path: z.string().nullable().optional(),
}).passthrough();
/**
 * Schema for the New Invoice dialog form.
 * Enforces required fields (vendor, invoice number, invoice date) for manual creation.
 */
export const newInvoiceDialogSchema = z.object({
    vendor_id: uuidSchema,
    invoice_number: z
        .string()
        .min(1, 'Invoice number is required')
        .max(MAX_INVOICE_NUMBER_LENGTH, `Invoice number must be ${MAX_INVOICE_NUMBER_LENGTH} characters or less`),
    invoice_date: dateSchema,
    service_date: optionalDateSchema,
    due_date: optionalDateSchema,
    po_number: z
        .string()
        .max(MAX_PO_NUMBER_LENGTH, `PO number must be ${MAX_PO_NUMBER_LENGTH} characters or less`)
        .optional()
        .or(z.literal(''))
        .nullable(),
    payment_terms: z
        .string()
        .max(MAX_PAYMENT_TERMS_LENGTH, `Payment terms must be ${MAX_PAYMENT_TERMS_LENGTH} characters or less`)
        .optional()
        .or(z.literal(''))
        .nullable(),
    currency: z
        .string()
        .max(MAX_CURRENCY_LENGTH, `Currency must be ${MAX_CURRENCY_LENGTH} characters or less`)
        .optional()
        .or(z.literal(''))
        .nullable(),
    notes: z
        .string()
        .max(MAX_NOTES_LENGTH, `Notes must be ${MAX_NOTES_LENGTH} characters or less`)
        .optional()
        .or(z.literal(''))
        .nullable(),
    discount_percent: z
        .union([z.number().min(0).max(100), z.literal('')])
        .optional()
        .nullable(),
    discount_expiration: optionalDateSchema,
    price_book_id: uuidSchema.optional().nullable(),
});
/**
 * Schema for updating an existing invoice.
 * All fields are optional for partial updates.
 */
export const invoiceUpdateSchema = invoiceCreateSchema.partial();
//# sourceMappingURL=invoiceSchema.js.map